import { useQuery } from "@tanstack/react-query";
import { trackClarityRestCall } from "../../analytics/trackClarityApi";

export interface DesktopInstallerFileDto {
  key: string;
  filename: string;
  sizeBytes: number;
  lastModified: string;
  downloadUrl: string;
  variant?: string | null;
}

export interface DesktopInstallersPayload {
  code: string;
  bucket: string;
  branch: string;
  limit: number;
  revisions: {
    revision: string;
    windows: DesktopInstallerFileDto | null;
    mac: DesktopInstallerFileDto[];
  }[];
}

/** Origin of the proxy API (same host as `REACT_APP_GQL_ENDPOINT`). */
const resolveProxyApiOrigin = (): string => {
  const gql = process.env.REACT_APP_GQL_ENDPOINT?.trim();
  if (!gql) return "";
  try {
    return new URL(gql).origin;
  } catch {
    return "";
  }
};

const fetchDesktopInstallers = async (
  origin: string
): Promise<DesktopInstallersPayload | null> => {
  const qs = new URLSearchParams();
  qs.set("limit", "3");
  try {
    const r = await fetch(
      `${origin}/api/public/desktop-installers?${qs.toString()}`,
      { method: "GET", credentials: "omit" }
    );
    if (!r.ok) {
      trackClarityRestCall(
        "GetDesktopInstallers",
        "network_error",
        `HTTP_${r.status}`
      );
      throw new Error(`HTTP ${r.status}`);
    }
    const data = (await r.json()) as DesktopInstallersPayload;
    if (data?.code !== "OK" || !Array.isArray(data.revisions)) {
      trackClarityRestCall(
        "GetDesktopInstallers",
        "business_failure",
        data?.code ?? "INVALID_PAYLOAD"
      );
      return null;
    }
    trackClarityRestCall("GetDesktopInstallers", "success", "OK");
    return data;
  } catch (error) {
    if (error instanceof Error && error.message.startsWith("HTTP ")) {
      throw error;
    }
    trackClarityRestCall("GetDesktopInstallers", "network_error");
    throw error;
  }
};

/**
 * Public GET /api/public/desktop-installers (presigned desktop CI artifacts).
 * @param fetchEnabled — pass `false` when embedded shell hides downloads (no request).
 */
export const useDesktopInstallers = (fetchEnabled: boolean) => {
  const origin = resolveProxyApiOrigin();
  const enabled = fetchEnabled && !!origin;

  return useQuery({
    queryKey: ["public", "desktop-installers", origin],
    queryFn: () => fetchDesktopInstallers(origin),
    enabled,
    staleTime: 5 * 60 * 1000
  });
};
